import { z } from 'zod';
import { MOTIVOS_BASURA } from '../enums.js';
// Tablero de KPIs (pedido de Javier, 16/09/2026): mensajes enviados/recibidos,
// tasa de respuesta, tasa de bloqueo y pendientes de contactar, con filtro de
// fecha y de campaña opcionales.
export const metricasQuerySchema = z.object({
    desde: z.coerce.date().optional(),
    hasta: z.coerce.date().optional(),
    campana: z.string().trim().min(1).optional(),
});
export const metricasPublicSchema = z.object({
    desde: z.string().nullable(),
    hasta: z.string().nullable(),
    campana: z.string().nullable(),
    mensajesEnviados: z.number().int(),
    mensajesRecibidos: z.number().int(),
    // Tasa de respuesta: de los leads a los que les escribimos en el rango,
    // qué % nos contestó después.
    leadsContactados: z.number().int(),
    leadsQueRespondieron: z.number().int(),
    tasaRespuesta: z.number(),
    // Tasa de bloqueo: leads eliminados (Estado: Bloqueado) sobre el total de
    // leads del rango/campaña. bloqueadoEn es la fecha por la que se filtra.
    leadsBloqueados: z.number().int(),
    totalLeads: z.number().int(),
    tasaBloqueo: z.number(),
    // Pendientes de contactar: de los leads del rango/campaña, cuántos nunca
    // recibieron un mensaje nuestro (sin importar cuándo).
    leadsPendientes: z.number().int(),
    leadsYaContactados: z.number().int(),
});
// Recontacto AstroYoga (17/09/2026, pedido de Javier): la campaña que corre el
// Emisor de n8n sobre Notion Master Leads. La app no la ve en su Postgres, así
// que estos números salen de Notion con el MISMO filtro que usa el Emisor:
// Fuente != Google Maps (Scraping) y con teléfono. "Contactado" = la marca
// `campaña astroyoga` en Notas; "bloqueado" = Estado: Bloqueado.
export const recontactoPublicSchema = z.object({
    tomadoEn: z.string(),
    // El universo: no scrapeados con teléfono.
    aContactar: z.number().int(),
    contactados: z.number().int(),
    pendientes: z.number().int(),
    convertidos: z.number().int(),
    // Tres cosas distintas que en Notion son el mismo Estado: Bloqueado (Javier, 17/09):
    //  - nosBloquearon: contactados por el Emisor que quedaron con un tilde >= 48 h
    //    (la Revisión de entregas escribe "No entregado 48h (posible bloqueo…)")
    //  - nosBloquearonAntes: marcados "me bloqueó" a mano ANTES de la campaña, sin la
    //    marca del Emisor. No entran en la tasa: nunca se les escribió.
    //  - losBloqueamos: eliminados desde la app ("[Bloqueado … — Agente-Ventas]").
    nosBloquearon: z.number().int(),
    nosBloquearonAntes: z.number().int(),
    losBloqueamos: z.number().int(),
    bloqueados: z.number().int(),
    // nosBloquearon / contactados por el Emisor: la tasa que dice si el ritmo hace daño.
    tasaBloqueo: z.number(),
    // Combinada: nosBloquearon (Notion) sobre la unión por teléfono de contactados
    // por la app y por el Emisor.
    contactadosTotal: z.number().int(),
    contactadosApp: z.number().int(),
    tasaBloqueoTotal: z.number(),
});
// Embudo de ventas + costos de Meta Ads (ciclo-metricas-embudo-y-contenido,
// 28/09/2026, pedido de Javier). Para AMBOS roles (a diferencia de arriba, que es
// solo ADMINISTRADOR): esta pantalla es específicamente para que la use Ailen.
// Solo cuenta leads `fuente = META_ADS` — dividir el gasto por leads que no
// vinieron del anuncio no tendría sentido (decisión 1 del proposal).
export const embudoQuerySchema = z.object({
    desde: z.coerce.date().optional(),
    hasta: z.coerce.date().optional(),
    // D-040: id de la campaña de la app (Clases AstroYoga, Taller AstroYoga...). Filtra
    // los leads y el gasto, que sale de las campañas de Meta de sus anuncios.
    campana: z.string().trim().min(1).optional(),
});
export const embudoPublicSchema = z.object({
    desde: z.string().nullable(),
    hasta: z.string().nullable(),
    // Conteo por etapa ACTUAL (1-5) — dónde está parado cada lead ahora mismo, sin
    // contar los que ya cerraron GANADO. No es acumulado: un lead que se cerró mal
    // (NO_INTERESADO/SIN_RESPUESTA/BLOQUEADO) a mitad de camino queda contando en la
    // etapa donde se estancó para siempre. Sirve para saber "dónde tengo gente hoy",
    // no para el embudo (ver `porEtapaAcumulado`).
    porEtapa: z.record(z.string(), z.number().int()),
    // El embudo real (D-034): para cada etapa N, cuántos leads llegaron ALGUNA VEZ a
    // esa etapa o más lejos (`etapaMaxima >= N` en la base), ganados incluidos. Al ser
    // acumulado nunca puede agrandarse a medida que N crece — a diferencia de `porEtapa`.
    porEtapaAcumulado: z.record(z.string(), z.number().int()),
    ganado: z.number().int(),
    // Desglose de cómo terminaron los leads que llegaron a la etapa 5 (D-035),
    // para el pie del embudo. `ganados + perdidos + enProceso === porEtapaAcumulado['5']`
    // siempre — las tres cuentas comparten el mismo filtro base (`etapaMaxima >= 5`).
    // `perdidos` agrupa CERRADO_NO_INTERESADO, CERRADO_SIN_RESPUESTA, DERIVADO_SALUD y
    // BLOQUEADO (todo cierre que no sea la venta); `enProceso` es el resto (todavía
    // conversando, en cualquier estado no cerrado).
    cierreDesglose: z.object({
        ganados: z.number().int(),
        perdidos: z.number().int(),
        enProceso: z.number().int(),
    }),
    gastoTotal: z.number(),
    // null (no 0 ni Infinity) cuando falta el denominador — la pantalla lo
    // muestra como "sin datos todavía", no como un costo real de $0 o de error.
    costoPorMensaje: z.number().nullable(),
    costoPorClienteCerrado: z.number().nullable(),
    // Gasto por día en el rango (28/09, pedido de Javier: gráfico de barras del
    // costo de campaña día a día, con el total ya disponible arriba en gastoTotal).
    gastoPorDia: z.array(z.object({ fecha: z.string(), spend: z.number() })),
});
// Indicadores de contenido orgánico (Buffer): promedio semanal entre los posts
// publicados esa semana, con el detalle de cada uno para el drill-down.
export const contenidoQuerySchema = z.object({
    desde: z.coerce.date().optional(),
    hasta: z.coerce.date().optional(),
});
export const contenidoPostSchema = z.object({
    id: z.string(),
    canal: z.string(),
    texto: z.string().nullable(),
    publicadoEn: z.string(),
    // null en Facebook: Meta deprecó reach/impressions orgánico de Page (D-029).
    // También null en YouTube (D-031): no hay cuentas únicas sin YouTube Analytics.
    reach: z.number().int().nullable(),
    // Reproducciones/vistas (D-031, 29/09): DISTINTO de reach (cuentas únicas) —
    // Buffer los reporta como dos métricas separadas para Instagram/TikTok, y
    // pueden diferir bastante en el mismo post. null solo en Facebook (Buffer no
    // lo reporta ahí).
    views: z.number().int().nullable(),
    interacciones: z.number().int(),
    tiempoReproduccionProm: z.number().nullable(),
});
export const contenidoSemanaSchema = z.object({
    semanaInicio: z.string(), // AAAA-MM-DD, lunes de esa semana
    cantidadPosts: z.number().int(),
    reachProm: z.number().nullable(),
    viewsProm: z.number().nullable(),
    interaccionesProm: z.number().nullable(),
    tiempoReproduccionProm: z.number().nullable(),
    posts: z.array(contenidoPostSchema),
});
export const contenidoPublicSchema = z.object({
    desde: z.string(),
    hasta: z.string(),
    semanas: z.array(contenidoSemanaSchema),
});
// Tablero de calidad por anuncio y por campaña (ciclo-calidad-leads, R5,
// 15/10/2026). Solo leads `fuente = META_ADS`. Las fechas filtran la creación
// del lead y el día del gasto de Meta. Para ambos roles, como el embudo.
export const calidadQuerySchema = z.object({
    desde: z.coerce.date().optional(),
    hasta: z.coerce.date().optional(),
    // D-040: id de la campaña de la app (Clases AstroYoga, Taller AstroYoga...). Filtra
    // los leads y el gasto, que sale de las campañas de Meta de sus anuncios.
    campana: z.string().trim().min(1).optional(),
});
export const calidadFilaSchema = z.object({
    // El id de Meta (anuncio o campaña); null en la fila "sin anuncio identificado".
    id: z.string().nullable(),
    nombre: z.string().nullable(),
    // Solo en las filas por anuncio: a qué campaña de Meta pertenece.
    campaignId: z.string().nullable(),
    campaignName: z.string().nullable(),
    leads: z.number().int(),
    // BUENO + COMPRO.
    buenos: z.number().int(),
    compraron: z.number().int(),
    basura: z.number().int(),
    basuraPorMotivo: z.record(z.enum(MOTIVOS_BASURA), z.number().int()),
    // Sin calificar y cerrados sin responder: se cuentan aparte, no son basura.
    sinRespuesta: z.number().int(),
    sinCalificar: z.number().int(),
    // Sobre buenos + basura (los calificados con un juicio). null si no hay ninguno.
    pctBuenos: z.number().nullable(),
    pctBasura: z.number().nullable(),
    gasto: z.number(),
    // gasto / buenos. null si no hay buenos (nunca infinito ni cero).
    costoPorLeadBueno: z.number().nullable(),
});
export const calidadPublicSchema = z.object({
    desde: z.string().nullable(),
    hasta: z.string().nullable(),
    porAnuncio: z.array(calidadFilaSchema),
    porCampana: z.array(calidadFilaSchema),
    total: calidadFilaSchema,
});
//# sourceMappingURL=metricas.js.map